import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_MY_CARDS_BOXES_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useOneTimeDialogContext } from '@sorare/core/src/contexts/oneTimeDialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { MilestonesDialog } from '@sorare/gameplay/src/components/home/LineupsWidget/CountdownWidget/MilestonesDialog';

import { LoyaltyBoxDialog } from './LoyaltyBoxDialog';
import type {
  loyaltyBoxButtonQuery,
  loyaltyBoxButtonQueryVariables,
} from './__generated__/index.graphql';

const DropdownOption = styled(Horizontal)`
  padding: var(--unit) var(--intermediate-unit);
`;

const LOYALTY_BOX_BUTTON_QUERY = gql`
  query loyaltyBoxButtonQuery {
    so5 {
      commonGameEndOfSeasonCountdown {
        countdownUntil
        ...MilestonesDialog_commonGameEndOfSeasonCountdown
      }
    }
  }
  ${MilestonesDialog.fragments.commonGameEndOfSeasonCountdown}
` as TypedDocumentNode<loyaltyBoxButtonQuery, loyaltyBoxButtonQueryVariables>;

export const LoyaltyBoxButton = () => {
  const { currentUser } = useCurrentUserContext();
  const { lifecycle } = useLifecycle();
  const hideCommonFeatures = lifecycle?.hideCommonFeatures;
  const [openDropdown, setOpenDropdown] = useState(false);
  const [milestonesDialogOpen, setMilestonesDialogOpen] = useState(false);
  const loyaltyBox = currentUser?.loyaltyBoxes.nodes[0];
  const time = loyaltyBox?.openableAt;

  const { data } = useQuery(LOYALTY_BOX_BUTTON_QUERY, {
    skip: hideCommonFeatures || !time,
  });
  const { commonGameEndOfSeasonCountdown } = data?.so5 || {};

  const { formatMessage } = useIntl();

  const { sawDialog } = useOneTimeDialogContext();

  const loyaltyBoxPath = generatePath(FOOTBALL_MY_CARDS_BOXES_TYPE, {
    type: 'loyalty',
  });

  if (!time) return null;

  if (commonGameEndOfSeasonCountdown?.countdownUntil) {
    return (
      <>
        <Dropdown
          onOpen={() => setOpenDropdown(true)}
          onClose={() => setOpenDropdown(false)}
          open={openDropdown}
          gap={4}
          label={props => (
            <Button
              size="medium"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--c-yellow-600) 10%, transparent)',
              }}
              {...props}
            >
              <span style={{ color: 'var(--c-yellow-600)' }}>
                <Gift />
              </span>
              <TimeLeft
                time={time}
                endLabel={formatMessage({
                  id: 'loyalty-box-button.end-label',
                  defaultMessage: 'Open your box',
                })}
              />
            </Button>
          )}
        >
          {({ closeDropdown }) => (
            <Vertical gap={0}>
              <Link to={loyaltyBoxPath}>
                <DropdownOption>
                  <ScarcityIcon rarity={Rarity.limited} size={2} />
                  <LabelL>
                    <FormattedMessage {...scarcityMessages.limited} />
                  </LabelL>
                </DropdownOption>
              </Link>
              <ButtonBase
                onClick={() => {
                  setMilestonesDialogOpen(true);
                  closeDropdown();
                }}
              >
                <DropdownOption>
                  <ScarcityIcon rarity={Rarity.common} size={2} />
                  <LabelL>
                    <FormattedMessage {...scarcityMessages.common} />
                  </LabelL>
                </DropdownOption>
              </ButtonBase>
            </Vertical>
          )}
        </Dropdown>
        <OneTimeDialog
          dialogId={LIFECYCLE.sawLoyaltyBoxDialog}
          show={!sawDialog(LIFECYCLE.sawLoyaltyBoxDialog)}
        >
          {({ onClose, open }) => (
            <LoyaltyBoxDialog
              loyaltyBox={loyaltyBox}
              onClose={onClose}
              open={open}
            />
          )}
        </OneTimeDialog>
        <MilestonesDialog
          open={milestonesDialogOpen}
          onClose={() => setMilestonesDialogOpen(false)}
          endOfSeasonCountdown={commonGameEndOfSeasonCountdown}
        />
      </>
    );
  }

  return (
    <>
      <Button
        to={loyaltyBoxPath}
        size="medium"
        style={{
          backgroundColor:
            'color-mix(in srgb, var(--c-yellow-600) 10%, transparent)',
        }}
      >
        <span style={{ color: 'var(--c-yellow-600)' }}>
          <Gift />
        </span>
        <TimeLeft
          time={time}
          endLabel={formatMessage({
            id: 'loyalty-box-button.end-label',
            defaultMessage: 'Open your box',
          })}
        />
      </Button>
      <OneTimeDialog
        dialogId={LIFECYCLE.sawLoyaltyBoxDialog}
        show={!sawDialog(LIFECYCLE.sawLoyaltyBoxDialog)}
      >
        {({ onClose, open }) => (
          <LoyaltyBoxDialog
            loyaltyBox={loyaltyBox}
            onClose={onClose}
            open={open}
          />
        )}
      </OneTimeDialog>
    </>
  );
};
